import React, { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

const STORAGE_KEY = "b2v_pwa_install_dismissed";

export function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const days = (Date.now() - Number(dismissed)) / (1000 * 60 * 60 * 24);
      if (days < 14) return; // re-show after 14 days
    }
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (ios) {
      setIsIOS(true);
      // Slight delay so it does not block first paint
      const t = setTimeout(() => setShow(true), 4000);
      return () => clearTimeout(t);
    }
    // Android / Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    close();
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      data-testid="pwa-install-banner"
    >
      <div className="rounded-xl border border-[#D926A9]/40 bg-[#1B132B]/95 backdrop-blur-md p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <img
            src="/icons/icon-192.png"
            alt="Bilan 2 Vie"
            className="w-12 h-12 rounded-lg shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#F7F3FC] mb-1">
              Installe Bilan 2 Vie sur ton téléphone
            </p>
            {isIOS ? (
              <p className="text-xs text-[#B59ED1] leading-relaxed">
                Touche{" "}
                <Share className="w-3.5 h-3.5 inline-block mx-0.5 text-[#00E5FF]" />{" "}
                en bas de Safari, puis « Sur l'écran d'accueil »{" "}
                <Plus className="w-3.5 h-3.5 inline-block mx-0.5 text-[#00E5FF]" />.
                Tu pourras recevoir les notifications de tes RDV.
              </p>
            ) : (
              <p className="text-xs text-[#B59ED1] leading-relaxed">
                Accède à tes RDV plus vite et reçois des notifications.
              </p>
            )}
            {!isIOS && deferredPrompt && (
              <button
                onClick={install}
                className="mt-2 px-3 py-1.5 rounded-full text-xs font-medium gradient-primary"
                data-testid="pwa-install-btn"
              >
                Installer
              </button>
            )}
          </div>
          <button
            onClick={close}
            aria-label="Fermer"
            className="text-[#B59ED1] hover:text-white"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
